import { useState, ChangeEvent, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { FieldError, useForm } from 'react-hook-form/dist/index.ie11';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import { DropdownWithLabel } from '@blueprint/dropdown';
import { TextField } from '@blueprint/text-input';
import triggerTracking from '@blueprint/tracking';

import { PropertiesInPostcodeQuery } from 'valuation/queries/propertiesInPostcode.generated';
import {
    CallToActionRow,
    PropertyBlock,
    TextFieldWrapper,
    StyledButtonWithIcon,
    PostcodeHint,
    SupportingCaption
} from './GetHomeWorthEstimateForm.styles';
import { validationDecoration } from '@zoopla/shared/forms';
import { redirect } from 'shared/helpers/redirect';
import { MAIN_SITE_LINK } from '../../../constants';
import QUERY_PROPERTIES_IN_POSTCODE from 'valuation/queries/propertiesInPostcode.graphql';
import QUERY_UPRN_PROPERTIES_IN_POSTCODE from 'valuation/queries/addressByPostcode.graphql';
import { SearchSmall } from '@blueprint/icons';
import useOptimizely from 'shared/hooks/optimizely/useOptimizely';
import { Address, AddressSearchApi } from 'graphql.generated';
import { POSTCODE_REGEX } from 'valuation/components/helpers/address';
import { getTrackedPropertyTaxonomyData } from 'my-account/components/TrackedPropertyCard/TrackedPropertyCard.tracking';

const defaultLabels = {
    postcodeLabel: 'Enter full postcode, e.g. SW1 0RG',
    addressLabel: 'Address',
    findAddressButtonText: 'Look up postcode',
    getEstimateButtonText: 'Check My Home'
};

const addressDropdownDefaultValue = 'Please select';
const noPostcodeError =
    "We do not recognise that postcode. Check it's for a UK address.";
const propertiesInPostcodeQueryError =
    'There was an error fetching properties for that postcode. Please try again.';
const invalidPostcodeValidationError =
    'Please provide a full, valid postcode e.g. AB1 2CD';

export type GetHomeWorthEstimateFormProps = {
    eventCategory: string;
    idSuffix?: string;
    labelId: string;
    initialPostcode?: string;
    shouldFocusAfterInitialSearch?: boolean;
    overriddenLabels?: {
        postcodeLabel?: string;
        addressCaption?: string;
        addressLabel?: string;
        findAddressButtonText?: string;
        getEstimateButtonText?: string;
        postcodeHint: string;
        postcodeCaption?: string;
    };
    onSearchDone?: () => void;
};

type PostcodesToProperties = Record<
    string,
    PropertiesInPostcodeQuery['properties']
>;

type PropertyInPostcodeQuery = PropertiesInPostcodeQuery['properties'][0];
type Properties = PropertiesInPostcodeQuery['properties'] | Address[];

export const formatPostcode = (postcode: string) => {
    const trimmedPostcode = postcode.trim();

    if (trimmedPostcode.length <= 3 || trimmedPostcode.indexOf(' ') > -1) {
        return trimmedPostcode;
    }

    const postcodeLength = trimmedPostcode.length;
    // the outcode is always 3 characters long
    const outcode = trimmedPostcode.slice(postcodeLength - 3, postcodeLength);
    const postcodeStart = trimmedPostcode.slice(0, postcodeLength - 3);

    return `${postcodeStart} ${outcode}`;
};

export const GetHomeWorthEstimateForm = ({
    eventCategory,
    idSuffix,
    labelId,
    initialPostcode = '',
    shouldFocusAfterInitialSearch = false,
    overriddenLabels,
    onSearchDone = () => null,
    optimizely
}: GetHomeWorthEstimateFormProps & WithOptimizelyProps) => {
    const [properties, setProperties] = useState<Properties>([]);
    const [isInitialSearchDone, setIsInitialSearchDone] = useState(
        initialPostcode ? false : true
    );
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
    const {
        handleSubmit,
        register,
        errors,
        formState: { touched, isSubmitting },
        setError,
        clearErrors,
        setValue,
        getValues
    } = useForm({
        mode: 'onBlur'
    });

    const labels = { ...defaultLabels, ...overriddenLabels };

    const getPropertiesByPostcode = async (
        data: AddressSearchApi | PostcodesToProperties
    ) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'click',
            eventLabel: 'locate_address'
        });

        const propertiesFromApi =
            (data as PostcodesToProperties)?.properties ||
            (data as AddressSearchApi)?.addressesByPostcode?.addresses;

        if (propertiesFromApi?.length) {
            setProperties(propertiesFromApi);
            clearErrors();

            // we want to avoid focus on the dropdown in specific cases
            const shouldAvoidFocus =
                !isInitialSearchDone && !shouldFocusAfterInitialSearch;
            if (!shouldAvoidFocus) {
                setTimeout(() => {
                    // the blueprint DropdownWithLabel does not forward ref
                    // so we target it by using its id
                    document.getElementById('property-select')?.focus();
                }, 200);
            }
        } else {
            setProperties([]);
            setError('postcode', {
                type: 'manual',
                message: noPostcodeError
            });
        }

        setIsInitialSearchDone(true);
        onSearchDone();
    };

    const [getPostCodeWithUPRN, { loading: loadingUPRNProperties }] =
        useLazyQuery(QUERY_UPRN_PROPERTIES_IN_POSTCODE, {
            onError: function () {
                setProperties([]);
                setError('postcode', {
                    type: 'manual',
                    message: propertiesInPostcodeQueryError
                });
                return;
            },
            onCompleted: getPropertiesByPostcode,
            fetchPolicy: 'cache-and-network'
        });

    const [getPostCode, { loading: loadingPropertyIDProperties }] =
        useLazyQuery(QUERY_PROPERTIES_IN_POSTCODE, {
            onError: function () {
                setProperties([]);
                setError('postcode', {
                    type: 'manual',
                    message: propertiesInPostcodeQueryError
                });
                return;
            },
            onCompleted: getPropertiesByPostcode,
            // `onCompleted` only fired once for the same query caused address fields not updated, a bug of @apollo/client, see: https://github.com/apollographql/apollo-client/issues/6636#issuecomment-666310747
            fetchPolicy: 'cache-and-network'
        });

    const redirectToPropertyDetailsPage = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'clickthrough',
            eventLabel: 'get_estimate',
            taxonomy: getTrackedPropertyTaxonomyData(
                isFeatureEnabled
                    ? { uprn: selectedPropertyId.replace('uprn/', '') }
                    : { propertyId: selectedPropertyId }
            )
        });

        const propertyDetailsPageRoute = `${MAIN_SITE_LINK}/property/${selectedPropertyId}/`;
        redirect(propertyDetailsPageRoute);
    };

    const hasFetchedPropertiesByPostcode = properties && properties.length > 0;
    const hasEnteredValidPostcode =
        getValues('postcode') &&
        getValues('postcode') !== '' &&
        !errors?.postcode;
    const hasSelectedAddressFromDropdown = selectedPropertyId;

    const buttonText = hasFetchedPropertiesByPostcode
        ? labels.getEstimateButtonText
        : labels.findAddressButtonText;

    const isButtonDisabled = hasFetchedPropertiesByPostcode
        ? !hasSelectedAddressFromDropdown
        : !hasEnteredValidPostcode;

    const handleChangePostcode = () => {
        setProperties([]);
        setSelectedPropertyId('');
    };

    const handleChangeAddress = (event: ChangeEvent<HTMLSelectElement>) => {
        const {
            target: { value: propertyId }
        } = event;

        setSelectedPropertyId(propertyId);
    };

    const isFeatureEnabled = useOptimizely(optimizely, 'uprn-first-pdp');

    const formInputProps = (field: 'postcode') => ({
        id: field,
        name: field,
        validationErrorMessage: (errors[field] as FieldError)?.message,
        validationStatus: validationDecoration(field, errors, touched)
    });

    const getPostcodeAddress = useCallback(() => {
        if (isFeatureEnabled) {
            getPostCodeWithUPRN({
                variables: { postcode: formatPostcode(getValues('postcode')) }
            });
        } else {
            getPostCode({
                variables: { postcode: getValues('postcode') }
            });
        }
    }, [getPostCode, getPostCodeWithUPRN, isFeatureEnabled, getValues]);

    const onSubmit = () => {
        if (hasFetchedPropertiesByPostcode) {
            redirectToPropertyDetailsPage();
            return;
        }

        getPostcodeAddress();
    };

    const getAddressOptionValue = (propertyId?: number, uprn?: string) => {
        if (isFeatureEnabled && uprn) {
            return `uprn/${uprn}`;
        }

        return String(propertyId);
    };

    useEffect(() => {
        if (initialPostcode && initialPostcode !== '') {
            setIsInitialSearchDone(false);
            setValue('postcode', initialPostcode, { shouldValidate: true });
            getPostcodeAddress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPostcode, isInitialSearchDone]);

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid="home-estimate-form"
        >
            <TextFieldWrapper>
                {labels.postcodeCaption ? (
                    <SupportingCaption>
                        {labels.postcodeCaption}
                    </SupportingCaption>
                ) : null}
                <TextField
                    {...formInputProps('postcode')}
                    id={idSuffix ? `postcode-${idSuffix}` : 'postcode'}
                    name="postcode"
                    label={labels.postcodeLabel}
                    data-testid={idSuffix ? `postcode-${idSuffix}` : 'postcode'}
                    onChange={handleChangePostcode}
                    ref={register({
                        required: invalidPostcodeValidationError,
                        pattern: {
                            value: POSTCODE_REGEX,
                            message: invalidPostcodeValidationError
                        }
                    })}
                    defaultValue={initialPostcode}
                    aria-describedby={labelId}
                />
                {labels.postcodeHint && !errors?.postcode ? (
                    <PostcodeHint>{labels.postcodeHint}</PostcodeHint>
                ) : null}
            </TextFieldWrapper>

            {hasFetchedPropertiesByPostcode ? (
                <PropertyBlock>
                    {labels.addressCaption ? (
                        <SupportingCaption>
                            {labels.addressCaption}
                        </SupportingCaption>
                    ) : null}
                    <DropdownWithLabel
                        hasBorderRadius
                        id="property-select"
                        data-testid={
                            idSuffix
                                ? `property-select-${idSuffix}`
                                : 'property-select'
                        }
                        label={labels.addressLabel}
                        defaultValue={addressDropdownDefaultValue}
                        onChange={handleChangeAddress}
                        aria-describedby={labelId}
                        data-cs-mask
                    >
                        <option value={addressDropdownDefaultValue} disabled>
                            {addressDropdownDefaultValue}
                        </option>
                        {properties.map((property) => (
                            <option
                                value={getAddressOptionValue(
                                    (property as PropertyInPostcodeQuery)
                                        ?.propertyId,
                                    (property as Address)?.uprn
                                )}
                                key={
                                    (property as Address)?.uprn ||
                                    (property as PropertyInPostcodeQuery)
                                        ?.propertyId
                                }
                                label={property?.fullAddress || ''}
                            >
                                {property?.fullAddress}
                            </option>
                        ))}
                    </DropdownWithLabel>
                </PropertyBlock>
            ) : null}
            <CallToActionRow>
                <StyledButtonWithIcon
                    variant="primary"
                    data-testid={
                        idSuffix
                            ? `estimate-button-${idSuffix}`
                            : 'estimate-button'
                    }
                    disabled={isButtonDisabled}
                    isLoading={
                        loadingUPRNProperties ||
                        loadingPropertyIDProperties ||
                        isSubmitting
                    }
                >
                    <SearchSmall />
                    {buttonText}
                </StyledButtonWithIcon>
            </CallToActionRow>
        </form>
    );
};

export default withOptimizely(GetHomeWorthEstimateForm);
